/**
 * Copyright © 2017-2023 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding the
 * use or licensing of K4D's copyrighted materials, please contact us at contact@k4d.com.br.
 * "Knowledge for Decisions (K4D)" and the slogan "Using data to make precise decisions" are
 * registered trademarks of Knowledge for Decisions (K4D).
 */

import React, { useState, ChangeEvent, useCallback, useEffect, useRef } from "react";

import { ExternalURL } from "@shared/constants";
import { PagePaths } from "@shared/constants";

import { AuthType, useAuthentication } from "@shared/contexts";

import { NavigateFunction, useNavigate, useLocation } from "react-router-dom";

import { AuthenticationForm } from "@components/ui";
import { Field as FormField } from "@components/ui";
import { TextButton } from "@components/ui";

import OAuth2Service from "@features/authentication/services/OAuth2Service";

import ExternalAuthentication from "@layouts/ExternalAuthentication";
import Divider from "@layouts/Divider";

import { ComposeURLWithGetQueryArguments } from "@shared/utils";

import { FF_NATIVE_LOGIN } from "@shared/utils/feature-flag";


import { useOAuth2 } from "@shared/contexts";
import { useRecaptchaContext } from "@shared/contexts/RecaptchaContext";
import { IdentityRequest, StartAuthenticationRequest } from "@features/authentication/services/OAuth2Request";
import { useAuthError } from "@shared/hooks/useAuthError";
import { delay, intervalMiliseconds, isSuccessTokenStepResponse, scheduleSubmittedReset } from "@shared/utils";
import { isDevelopment } from "@shared/utils/environment";
import { TokenExpirationChecker } from "@shared/utils/token/TokenExpirationChecker";
import { useTranslation } from "react-i18next";

import { t_identity } from "@i18n/usualTexts/t_identity";
import i18n from "@i18n/index";
import {
  cleanFormDataErrors,
  set_localStorage,
} from "../../../shared/utils";
import { ActionButton } from "@components/ui";
import { useIsMobileButtons } from "@shared/hooks/useIsMobileButtons";
import { useDocumentTitle } from "@shared/hooks/useDocumentTitle";

function extractRecaptchaToken(recaptchaResult: { success: boolean; token?: string; error?: string }): string {
  const tokenObtained = recaptchaResult.success && !!recaptchaResult.token;
  if (!tokenObtained) {
    throw new Error(`${i18n.t('error.runtime.recaptchaFailed')}: ${recaptchaResult.error || 'Unknown error'}. ${i18n.t('error.runtime.antiBotRequired')}`);
  }
  return recaptchaResult.token!;
}

function shouldDelayForInterval(intervalMs: number): boolean {
  return intervalMs > 0 && intervalMs < 5000;
}

interface FormData {
  username: {
    value: string;
    errors: Set<string>;
  };
}

const IdentityPage: React.FC = () => {
  const { t } = useTranslation();
  useDocumentTitle(t("login.identity.pageTitle"));

  const navigate = useNavigate();
  const location = useLocation();
  const { handleOAuth2Error } = useAuthError();
  const isMobileButtons = useIsMobileButtons(); // For button positioning

  const [submitted, setSubmitted] = useState(false);
  const [serverErrorPopUp, setServerErrorPopUp] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const submittedResetRef = useRef<number | null>(null);

  const { oauth2Data, startAuthenticationRequestResponse } = useOAuth2();
  const { authData, setAuthData } = useAuthentication();
  const { currentToken: antiBotToken, executeRecaptcha } = useRecaptchaContext();

  const [formData, setFormData] = useState<FormData>({
    username: {
      value: authData.username ?? localStorage.getItem("username") ?? "",
      errors: new Set<string>(),
    },
  });

  // Check for token expiration parameter
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    if (queryParams.get("tokenExpired") === "true" || queryParams.get("sessionExpired") === "true") {
      // Show error message for token expiration
      setFormData((prevFormData) => {
        const newFormData = { ...prevFormData };
        newFormData.username.errors.add(t("warningTexts.oAuth2.tokenExpired"));
        return newFormData;
      });

      // Clear the URL parameters to avoid showing the error again on refresh
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete("tokenExpired");
      newUrl.searchParams.delete("sessionExpired");
      window.history.replaceState({}, "", newUrl.toString());
    }
  }, [location.search, t]);

  // Cleanup animation frame on unmount
  useEffect(() => {
    return () => {
      if (submittedResetRef.current !== null) {
        cancelAnimationFrame(submittedResetRef.current);
      }
    };
  }, []);

  const handleFormData = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    formData.username.value = value;
    setFormData({ ...formData });
    cleanFormDataErrors(formData);
  }, [formData]);

  const getAntiBotToken = useCallback(async (): Promise<string> => {
    if (!executeRecaptcha) {
      throw new Error(t('error.runtime.recaptchaUnavailable'));
    }

    const recaptchaResult = await executeRecaptcha();
    return extractRecaptchaToken(recaptchaResult);
  }, [executeRecaptcha, t]);

  const refreshTokenIfNeeded = useCallback(async (token: any): Promise<any> => {
    const needsRefresh = isSuccessTokenStepResponse(token) && TokenExpirationChecker.isTokenExpired(token, 30);
    if (!needsRefresh) {
      return token;
    }

    const tokenToUse = await getAntiBotToken();
    try {
      return await StartAuthenticationRequest(oauth2Data, undefined, tokenToUse);
    } catch (error: any) {
      const isAntiBotError = error?.status === 401 || error?.body?.title === 'AntiBotInvalidTokenError';
      if (!isAntiBotError) throw error;
      const freshToken = await getAntiBotToken();
      return await StartAuthenticationRequest(oauth2Data, undefined, freshToken);
    }
  }, [oauth2Data, getAntiBotToken]);

  const postFormDatas_and_navigate = useCallback(async (
    newAuthData: AuthType
  ) => {
    if (isDevelopment()) {
      console.log('[Identity] Clearing previous authentication data to prevent code reuse');
    }

    let currentStartToken = await refreshTokenIfNeeded(startAuthenticationRequestResponse);

    if (!isSuccessTokenStepResponse(currentStartToken)) {
      throw new Error(t("error.runtime.invalidAuthToken"));
    }

    const intervalMilisecondsValue = intervalMiliseconds(currentStartToken);
    if (shouldDelayForInterval(intervalMilisecondsValue)) {
      await delay(intervalMilisecondsValue);
    }

    const freshToken = await getAntiBotToken();

    const identityPostResponse = await IdentityRequest(
      oauth2Data,
      formData.username.value,
      currentStartToken.access_token,
      freshToken
    );
    console.log(identityPostResponse);

    const updatedAuthData: AuthType = {
      ...newAuthData,
      identityRequestResponse: identityPostResponse,
    };

    setAuthData(updatedAuthData);

    navigate(ComposeURLWithGetQueryArguments(PagePaths.LOGIN.PASSWORD));
  }, [oauth2Data, startAuthenticationRequestResponse, formData.username.value, setAuthData, navigate, refreshTokenIfNeeded, getAntiBotToken]);

  const handleSubmit = useCallback(async () => {
    // Set loading state immediately for instant UI feedback
    setIsLoading(true);
    setSubmitted(true);

    try {
      const step = 2;

      const newAuthData: AuthType = {
        ...authData,
        username: formData.username.value,
        step: step,
      };

      const authenticationService: OAuth2Service = new OAuth2Service();
      authenticationService.checkIdentityFormat(formData.username.value);

      await postFormDatas_and_navigate(newAuthData);
    } catch (e) {
      const error = JSON.parse(JSON.stringify(e));
      handleOAuth2Error(
        "username",
        error,
        setServerErrorPopUp,
        formData.username.errors
      );

      // Removed unused newAuthData variable
    } finally {
      setIsLoading(false);
      set_localStorage(formData);

      setFormData({
        username: {
          ...formData.username,
        },
      });

      scheduleSubmittedReset(submittedResetRef, setSubmitted);
    }
  }, [authData, formData, postFormDatas_and_navigate, handleOAuth2Error]);

  // Handle mobile form submission when button is outside form
  const handleMobileSubmit = useCallback(() => {
    if (isLoading) {
      return;
    }

    // Find the form element and trigger submission
    const formElement = document.querySelector('form');
    if (formElement) {
      // Create a synthetic submit event and dispatch it
      const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
      formElement.dispatchEvent(submitEvent);
    }
  }, [isLoading]);

  const recaptchaNotice = (
    <p className="text-[10px] text-left leading-tight" style={{ color: 'var(--text-tertiary)' }}>
      {t("login.identity.recaptchaNotice")}
    </p>
  );

  const identityActionRow = (isMobile: boolean) => (
    <div className="flex items-center gap-3">
      <div className="flex-1">
        <TextButton
          variant="secondary"
          className="w-full h-11"
          onClick={() => {
            toCreatePage(navigate);
          }}
        >
          {t(t_identity.createAccountButton)}
        </TextButton>
      </div>
      <div className="flex-1">
        <ActionButton
          submit
          isLoading={isLoading}
          className={isMobile ? "h-11 w-full" : "w-full"}
          onClick={isMobile ? handleMobileSubmit : undefined}
        />
      </div>
    </div>
  );

  const mobileFooterContent = FF_NATIVE_LOGIN && isMobileButtons
    ? (
      <div className="flex flex-col gap-2">
        {identityActionRow(true)}
        {recaptchaNotice}
      </div>
    )
    : undefined;

  return (
    <AuthenticationForm
      onSubmit={handleSubmit}
      isLoading={isLoading}
      serverErrorPopUp={serverErrorPopUp}
      setServerErrorPopUp={setServerErrorPopUp}
      mobileFooter={mobileFooterContent}
    >
      <ExternalAuthentication />

      {FF_NATIVE_LOGIN && (
        <>
          <Divider>{t("login.identity.orConnectWithText")}</Divider>

          <FormField
            autofocus={true}
            name="username"
            type="text"
            required={true}
            onChange={handleFormData}
            value={formData.username.value}
            errors={formData.username.errors}
            submitted={submitted}
            placeholder={t("login.identity.emailPlaceholderHint")}
          >
            {t(t_identity.emailPlaceholder)}
          </FormField>
        </>
      )}

      <p className="text-[13px] leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
        {t(t_identity.guestModeText)}{" "}
        <TextButton url={ExternalURL.MORE_DETAILS}>
          {t(t_identity.guestModeLink)}
        </TextButton>
      </p>

      {FF_NATIVE_LOGIN
        ? (!isMobileButtons && (
          <div className="flex flex-col gap-2">
            {identityActionRow(false)}
            {recaptchaNotice}
          </div>
        ))
        : recaptchaNotice}
    </AuthenticationForm>
  );
};

function toCreatePage(navigate: NavigateFunction) {
  navigate(ComposeURLWithGetQueryArguments(PagePaths.SIGNUP.CREATEACCOUNT), {
    replace: true,
  });
}

export default IdentityPage;
